import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import {
  AdminModal,
  AdminInput,
  AdminSelect,
  AdminTextarea,
  StatusBadge,
  formatDate,
  toDateInput,
} from '../../components/admin/AdminUI';
import { Plus, Pencil, X } from 'lucide-react';

const STATUSES = ['confirmed', 'pending', 'active', 'completed', 'cancelled'];
const PAYMENT_STATUSES = ['paid', 'pending', 'failed'];

const emptyForm = () => ({
  equipment_id: '',
  equipment_name: '',
  start_date: '',
  end_date: '',
  rental_type: 'daily',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  rental_amount: '',
  deposit_amount: '0',
  total_amount: '',
  status: 'confirmed',
  payment_status: 'paid',
  notes: '',
});

export const AdminRentals = () => {
  const { authedAxios } = useAdminAuth();
  const [rentals, setRentals] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([
        authedAxios().get('/api/admin/rentals'),
        authedAxios().get('/api/admin/equipment'),
      ]);
      setRentals(r.data.rentals || []);
      setEquipment(e.data.equipment || []);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(emptyForm());
    setModal({ mode: 'add' });
  };

  const openEdit = (r) => {
    setForm({
      ...emptyForm(),
      ...r,
      start_date: toDateInput(r.start_date),
      end_date: toDateInput(r.end_date),
      rental_amount: r.rental_amount ?? '',
      deposit_amount: r.deposit_amount ?? '0',
      total_amount: r.total_amount ?? '',
      notes: r.notes || '',
    });
    setModal({ mode: 'edit', rental: r });
  };

  const close = () => {
    setModal(null);
    setForm(emptyForm());
  };

  const save = async () => {
    setSaving(true);
    try {
      const eq = equipment.find((e) => e.id === form.equipment_id);
      const payload = {
        ...form,
        equipment_name: eq?.name || form.equipment_name,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : '',
        end_date: form.end_date ? new Date(form.end_date).toISOString() : '',
        rental_amount: form.rental_amount === '' ? null : parseFloat(form.rental_amount),
        deposit_amount: form.deposit_amount === '' ? 0 : parseFloat(form.deposit_amount),
        total_amount: form.total_amount === '' ? null : parseFloat(form.total_amount),
      };
      if (modal.mode === 'add') {
        await authedAxios().post('/api/admin/rentals', payload);
      } else {
        await authedAxios().patch(`/api/admin/rentals/${modal.rental.id}`, payload);
      }
      close();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (r) => {
    if (!window.confirm(`Cancel rental for ${r.first_name} ${r.last_name}?`)) return;
    try {
      await authedAxios().delete(`/api/admin/rentals/${r.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const onField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (loading) return <p className="text-[#F4F1EA]/60">Loading rentals...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-rentals-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-[#F4F1EA]">Equipment Rentals</h2>
          <p className="text-[#F4F1EA]/60 mt-1">{rentals.length} total</p>
        </div>
        <Button
          data-testid="admin-add-rental-btn"
          onClick={openAdd}
          className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Rental
        </Button>
      </div>

      <div className="overflow-x-auto bg-[#0B132B] border border-[#B8860B]/30 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#050814] text-[#B8860B] uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Equipment</th>
              <th className="text-left p-3">Dates</th>
              <th className="text-left p-3">Total</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Payment</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rentals.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-[#F4F1EA]/60">
                  No rentals yet.
                </td>
              </tr>
            )}
            {rentals.map((r) => (
              <tr
                key={r.id}
                data-testid={`admin-rental-row-${r.id}`}
                className="border-t border-[#B8860B]/10 hover:bg-[#050814]/60"
              >
                <td className="p-3">
                  <p className="font-semibold text-[#F4F1EA]">
                    {r.first_name} {r.last_name}
                  </p>
                  <p className="text-xs text-[#F4F1EA]/60">{r.email}</p>
                  <p className="text-xs text-[#F4F1EA]/60">{r.phone}</p>
                </td>
                <td className="p-3 text-[#F4F1EA]/85">
                  {r.equipment_name}
                  <p className="text-xs text-[#F4F1EA]/60">
                    {r.rental_days} days • {r.rental_type}
                  </p>
                </td>
                <td className="p-3 text-[#F4F1EA]/85">
                  <p>{formatDate(r.start_date)}</p>
                  <p className="text-xs text-[#F4F1EA]/60">→ {formatDate(r.end_date)}</p>
                </td>
                <td className="p-3 text-[#B8860B] font-semibold">
                  ${(r.total_amount || 0).toFixed(2)}
                </td>
                <td className="p-3">
                  <StatusBadge status={r.status} />
                </td>
                <td className="p-3">
                  <StatusBadge status={r.payment_status} />
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-edit-rental-${r.id}`}
                    onClick={() => openEdit(r)}
                    className="inline-flex items-center px-2 py-1 text-[#B8860B] hover:bg-[#B8860B]/15 rounded mr-1"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  {r.status !== 'cancelled' && (
                    <button
                      data-testid={`admin-cancel-rental-${r.id}`}
                      onClick={() => cancel(r)}
                      className="inline-flex items-center px-2 py-1 text-red-400 hover:bg-red-500/15 rounded"
                      title="Cancel"
                    >
                      <X size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminModal
        testid="admin-rental-modal"
        open={!!modal}
        onClose={close}
        title={modal?.mode === 'add' ? 'Add Rental' : 'Edit Rental'}
        footer={
          <>
            <Button variant="outline" onClick={close} className="border-[#B8860B]/40 text-[#F4F1EA]">
              Cancel
            </Button>
            <Button
              data-testid="admin-rental-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <AdminSelect label="Equipment" value={form.equipment_id} onChange={onField('equipment_id')}>
              <option value="">— Select equipment —</option>
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.name} ({eq.category})
                </option>
              ))}
            </AdminSelect>
          </div>
          <AdminInput label="Start Date" type="date" value={form.start_date} onChange={onField('start_date')} required />
          <AdminInput label="End Date" type="date" value={form.end_date} onChange={onField('end_date')} required />
          <AdminSelect label="Rental Type" value={form.rental_type} onChange={onField('rental_type')}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly (7 days)</option>
          </AdminSelect>
          <AdminInput label="Total ($)" type="number" step="0.01" value={form.total_amount} onChange={onField('total_amount')} />
          <AdminInput label="Rental Amount ($)" type="number" step="0.01" value={form.rental_amount} onChange={onField('rental_amount')} />
          <AdminInput label="Deposit ($)" type="number" step="0.01" value={form.deposit_amount} onChange={onField('deposit_amount')} />
          <AdminInput label="First Name" value={form.first_name} onChange={onField('first_name')} required />
          <AdminInput label="Last Name" value={form.last_name} onChange={onField('last_name')} required />
          <AdminInput label="Email" type="email" value={form.email} onChange={onField('email')} required />
          <AdminInput label="Phone" value={form.phone} onChange={onField('phone')} required />
          <AdminSelect label="Status" value={form.status} onChange={onField('status')}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </AdminSelect>
          <AdminSelect label="Payment" value={form.payment_status} onChange={onField('payment_status')}>
            {PAYMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </AdminSelect>
        </div>
        <AdminTextarea label="Internal Notes" value={form.notes} onChange={onField('notes')} />
      </AdminModal>
    </div>
  );
};

export default AdminRentals;
